import React, { useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  CircularProgress,
  Tooltip,
} from '@material-ui/core';

const ACTIONS = {
  EDIT: 'Edit',
  CREATE: 'Create',
  UPDATE: 'Update',
};

export function CreateUpdateEntityDialog(props) {
  const {
    entity,
    EditorComponent,
    entityType,
    initialData,
    referenceData,
    saveButtonText,
    onSave,
    onClose,
    getEntityLabel,
    defaultText,
    additionalActions,
    dialogSize,
    ...restProps
  } = props;
  const [localEntity, setLocalEntity] = useState({ ...initialData, ...entity });
  const [isSaving, setIsSaving] = useState(false);
  const [isPerformingAction, setIsPerformingAction] = useState(false);
  const [isValid, setIsValid] = useState(false);
  const saveButtonDisabled = useMemo(
    () => isSaving || !isValid,
    [isSaving, isValid]
  );
  const title = useMemo(
    () =>
      entity
        ? `${ACTIONS.EDIT}: ${getEntityLabel(entity)}`
        : `${ACTIONS.CREATE}: ${entityType}`,
    [entity, entityType, getEntityLabel]
  );
  const calculatedSaveButtonText = useMemo(
    () => saveButtonText || (entity ? ACTIONS.UPDATE : ACTIONS.CREATE),
    [saveButtonText, entity]
  );
  const handleValidityChange = useCallback(
    (validity) => setIsValid(validity),
    []
  );
  const handlePropertyChange = (changes) =>
    setLocalEntity((existing) => ({ ...existing, ...changes }));
  const handleSave = async () => {
    try {
      setIsSaving(true);
      await onSave(localEntity);
      onClose();
    } catch (error) {
      setIsSaving(false);
    }
  };
  const getHandler = (onClick) => async () => {
    try {
      setIsPerformingAction(true);
      await onClick(localEntity);
    } catch (error) {
      setIsPerformingAction(false);
    }
  };
  const disableAdditionalActions = useMemo(
    () =>
      additionalActions.map(({ disableAction }) =>  disableAction(localEntity)),
    [localEntity, additionalActions]
  );
  const displayAdditionalActions = useMemo(
    () =>
      additionalActions.map(({ displayAction }) => displayAction(localEntity)),
    [localEntity, additionalActions]
  );
  const calculatedSaveButtonTooltip = useMemo(
    () => `${saveButtonDisabled ? 'Invalid' : 'Save'} ${entityType}`,
    [saveButtonDisabled, entityType]
  );

  return (
    <Dialog
      scroll="paper"
      maxWidth={dialogSize}
      fullWidth
      open
      onClose={onClose}
    >
      <DialogTitle id="simple-dialog-title">{title}</DialogTitle>
      <DialogContent>
        <EditorComponent
          entity={localEntity}
          referenceData={referenceData}
          defaultText={defaultText}
          onPropertyChange={handlePropertyChange}
          onValidityChange={handleValidityChange}
          {...restProps}
        />
      </DialogContent>
      <DialogActions>
        <Tooltip title={`Close: ${title}`}>
          <Button data-testid="close-button" color="primary" onClick={onClose}>
            Close
          </Button>
        </Tooltip>
        <Tooltip title={calculatedSaveButtonTooltip}>
          <span>
            <Button
              data-testid="save-button"
              disabled={saveButtonDisabled}
              color="primary"
              variant="outlined"
              onClick={handleSave}
              startIcon={isSaving ? <CircularProgress /> : null}
            >
              {calculatedSaveButtonText}
            </Button>
          </span>
        </Tooltip>
        {additionalActions.map(
            (
              {
                onClick,
                message = '',
                displayAction = true,
                disableAction = false,
                tooltip,
                disabledTooltip,
                ...restProps
              },
              i
            ) =>
              displayAdditionalActions[i] && (
                <Tooltip
                  key={i}
                  title={
                    disableAdditionalActions[i] || saveButtonDisabled ? disabledTooltip : tooltip
                  }
                >
                  <span >
                    <Button
                      disabled={
                        saveButtonDisabled || disableAdditionalActions[i]
                      }
                      onClick={getHandler(onClick)}
                      {...restProps}
                    >
                      {message}
                    </Button>
                  </span>
                </Tooltip>
              )
          )}
      </DialogActions>
    </Dialog>
  );
}

CreateUpdateEntityDialog.propTypes = {
  /** The entity that we are creating/updating */
  // eslint-disable-next-line react/forbid-prop-types
  entity: PropTypes.object,
  /** Type of entity for displaying in the page title */
  entityType: PropTypes.string,
  /** Component that displays the entity specific create/update form */
  EditorComponent: PropTypes.func.isRequired,
  /** Default property values that creating a new entity starts with */
  // eslint-disable-next-line react/forbid-prop-types
  initialData: PropTypes.object,
  // eslint-disable-next-line react/forbid-prop-types
  referenceData: PropTypes.object,
  /** Default text for something */
  defaultText: PropTypes.string,
  /** Function called when the user clicks the close button */
  onClose: PropTypes.func.isRequired,
  /** Function called when the user clicks the create/update button */
  onSave: PropTypes.func.isRequired,
  /** Text that is displayed in the save button */
  saveButtonText: PropTypes.string,
  /** A function that returns a label for the given entity (default function returns the title or name property) */
  getEntityLabel: PropTypes.func,
  /** Array of components and properties used to display a set of additional actions alongside the create/update and close buttons */
  additionalActions: PropTypes.arrayOf(PropTypes.shape({
    /** Function called when the user clicks the button */
    onClick: PropTypes.func.isRequired,
    /** Message that the button displays */
    message: PropTypes.string.isRequired,
    /** Boolean determining when the button is visible */
    displayAction: PropTypes.func,
    /** Boolean determining when the button is disabled */
    disableAction: PropTypes.func,
    /** Tooltip message */
    tooltip: PropTypes.string,
    /** Tooltip message when the button is disabled */
    disabledTooltip: PropTypes.string,
  })),
  /** A string stating the size of the dialog */
  dialogSize: PropTypes.string,
};

CreateUpdateEntityDialog.defaultProps = {
  entity: null,
  initialData: null,
  referenceData: null,
  saveButtonText: null,
  getEntityLabel: ({ title, name }) => title || name || '',
  defaultText: '',
  additionalActions: [],
  dialogSize: 'lg',
};